import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import Modal from '../../layout/Modal';
import ActionButton from '../../primitives/ActionButton';
import FilterField from './FilterField';
import SavePresetPopover from './SavePresetPopover';
import { buildDefaultFilters } from './filterUtils';

const FilterModal = ({
  open,
  onClose,
  onApply,
  filterConfig = [],
  currentFilters = {},
  optionsMap = {},
  title = 'Filters',
  showExecutionScope = false,
  ExecutionScopeComponent = null,
  onExecutionScopeChange,
  savedFilters = [],
  onSaveFilter,
  onDeleteFilter,
  savingFilter = false,
  presetWarning = null,
  userRole = 'member',
}) => {
  const [localFilters, setLocalFilters] = useState(currentFilters);
  const [activePresetId, setActivePresetId] = useState(null);
  const [presetMenuAnchor, setPresetMenuAnchor] = useState(null);
  const [savePopoverAnchor, setSavePopoverAnchor] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const saveButtonRef = useRef(null);

  const hasSavedFilters = Boolean(onSaveFilter);
  const activePreset = savedFilters.find((p) => p.id === activePresetId) ?? null;

  useEffect(() => {
    if (open) {
      setLocalFilters(currentFilters);
      setActivePresetId(null);
      setPresetMenuAnchor(null);
      setSavePopoverAnchor(null);
      setSaveError(null);
      setWarningDismissed(false);
    }
  }, [open, currentFilters]);

  const handleFieldChange = (key, value) => {
    setLocalFilters((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === 'status' && 'reviewer' in prev) updated.reviewer = [];
      return updated;
    });
    setActivePresetId(null);
  };

  const handleReset = () => {
    setLocalFilters(buildDefaultFilters(filterConfig));
    setActivePresetId(null);
    setWarningDismissed(false);
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleLoadPreset = (preset) => {
    setLocalFilters({ ...buildDefaultFilters(filterConfig), ...preset.filters });
    setActivePresetId(preset.id);
    setPresetMenuAnchor(null);
    setWarningDismissed(false);
  };

  const handleDeletePreset = (e, id) => {
    e.stopPropagation();
    onDeleteFilter?.(id);
    if (activePresetId === id) setActivePresetId(null);
  };

  // shareLevel is 'private' | 'project' | 'organization'
 const handleSavePreset = async (name, shareLevel) => {
  setSaveError(null);
  try {
      await onSaveFilter(name, localFilters, shareLevel);
    setSavePopoverAnchor(null);
  } catch (err) {
    setSaveError(err?.message || 'Failed to save preset');
  }
};

  const visibleFields = filterConfig.filter((field) =>
    typeof field.visibleWhen === 'function'
      ? field.visibleWhen(localFilters)
      : true,
  );

  const renderActions = () => (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <ActionButton
          intent="ghost"
          size="small"
          icon={<RestartAltIcon fontSize="small" />}
          label="Reset All"
          onClick={handleReset}
          tooltip="Reset all filters to defaults"
        />
        {hasSavedFilters && (
          <>
            <Box ref={saveButtonRef} display="inline-flex">
              <ActionButton
                intent="ghost"
                size="small"
                icon={<BookmarkAddOutlinedIcon fontSize="small" />}
                label="Save Filter"
                onClick={() => {
                  setSaveError(null);
                  setSavePopoverAnchor(saveButtonRef.current);
                }}
                tooltip="Save current filters"
              />
            </Box>
            <SavePresetPopover
              open={Boolean(savePopoverAnchor)}
              anchorEl={savePopoverAnchor}
              onClose={() => setSavePopoverAnchor(null)}
              onSave={handleSavePreset}
              saving={savingFilter}
              error={saveError}
              userRole={userRole}
            />
          </>
        )}
      </Box>
      <Box display="flex" gap={1}>
        <ActionButton intent="secondary" size="small" label="Cancel" onClick={onClose} />
        <ActionButton intent="primary" size="small" label="Apply Filters" onClick={handleApply} />
      </Box>
    </Box>
  );

  return (
    <Modal
      open={open}
      handleClose={onClose}
      modalTitle={title}
      titleIcon={<FilterAltOutlinedIcon fontSize="small" />}
      width="md"
      disableBackdropClose
      contentSx={{ overflow: 'auto', px: 3, py: 2.5 }}
      action={renderActions}
    >
      {/* ── Saved filters dropdown ── */}
      {hasSavedFilters && (
        <Box mb={2.5} display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            color={activePreset ? 'primary' : 'inherit'}
            startIcon={<BookmarksOutlinedIcon sx={{ fontSize: '16px !important' }} />}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '16px !important' }} />}
            onClick={(e) => setPresetMenuAnchor(e.currentTarget)}
            sx={{
              textTransform: 'none',
              fontWeight: activePreset ? 600 : 400,
              borderColor: activePreset ? 'primary.main' : 'divider',
              color: activePreset ? 'primary.main' : 'text.secondary',
              bgcolor: activePreset ? 'primary.50' : 'transparent',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
            }}
          >
            {activePreset ? activePreset.name : 'Saved Filters'}
          </Button>

          <Menu
            anchorEl={presetMenuAnchor}
            open={Boolean(presetMenuAnchor)}
            onClose={() => setPresetMenuAnchor(null)}
            PaperProps={{ elevation: 3, sx: { minWidth: 240, maxHeight: 320, borderRadius: 1.5 } }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {savedFilters.length === 0 ? (
              <MenuItem disabled>
                <ListItemText
                  primary="No saved filters yet"
                  primaryTypographyProps={{ variant: 'body2', color: 'text.disabled' }}
                />
              </MenuItem>
            ) : (
              savedFilters.map((preset) => {
                const isActive = activePresetId === preset.id;
                return (
                  <MenuItem
                    key={preset.id}
                    onClick={() => handleLoadPreset(preset)}
                    selected={isActive}
                    sx={{ pr: 1, gap: 1 }}
                  >
                    <ListItemText
                      primary={preset.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isActive ? 600 : 400,
                        noWrap: true,
                      }}
                    />
                    <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                      {isActive && (
                        <CheckIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                      )}
                      <Tooltip title="Delete filter" placement="right">
                        <Box
                          component="span"
                          onClick={(e) => handleDeletePreset(e, preset.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '50%',
                            p: 0.25,
                            color: 'text.disabled',
                            cursor: 'pointer',
                            '&:hover': { color: 'error.main', bgcolor: 'error.50' },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </Tooltip>
                    </Box>
                  </MenuItem>
                );
              })
            )}
          </Menu>

          {presetWarning && !warningDismissed && (
            <Alert
              severity="warning"
              onClose={() => setWarningDismissed(true)}
              variant="outlined"
              sx={{ py: 0, px: 1.5, fontSize: '0.8rem', borderRadius: 1.5, '& .MuiAlert-icon': { fontSize: 16 }, '& .MuiAlert-action': { pt: 0 } }}
            >
              {presetWarning}
            </Alert>
          )}
        </Box>
      )}

      {/* ── Filter fields ── */}
      <Grid container spacing={2.5}>
        {visibleFields.map((field) => (
          <Grid item xs={12} sm={field.gridSize || 4} key={field.key}>
            <FilterField
              field={field}
              value={localFilters[field.key]}
              onChange={handleFieldChange}
              optionsMap={optionsMap}
            />
          </Grid>
        ))}
      </Grid>

      {showExecutionScope && ExecutionScopeComponent && (
        <>
          <Divider sx={{ my: 3 }} />
          {React.cloneElement(ExecutionScopeComponent, {
            values: localFilters,
            setFieldValue: (field, value) => {
              handleFieldChange(field, value);
              onExecutionScopeChange?.(field, value);
            },
            isPlatformRequired: false,
          })}
        </>
      )}
    </Modal>
  );
};

export default FilterModal;